import { newPasswordProblem } from "../../password";
import { t, translateMessage } from "../../i18n";
import { useState } from "react";
import { Eye, EyeOff, LogOut, ShieldCheck, ShieldOff, Smartphone } from "lucide-react";
import type { User } from "../../types";

type Props = {
  back: React.ReactNode;
  currentUser: User;
  onChangePassword: (password: string) => Promise<void>;
  onEnableMfa: () => Promise<void>;
  onDisableMfa: () => Promise<void>;
  onLogout: () => void;
};

export function AccountPage({
  back,
  currentUser,
  onChangePassword,
  onEnableMfa,
  onDisableMfa,
  onLogout
}: Props) {
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showRepeat, setShowRepeat] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [enablingMfa, setEnablingMfa] = useState(false);
  const [disablingMfa, setDisablingMfa] = useState(false);

  const save = async (): Promise<void> => {
    if (newPasswordProblem(password)) {
      setNote(newPasswordProblem(password));
      return;
    }
    if (password !== repeat) {
      setNote(t("Οι δύο κωδικοί πρόσβασης δεν ταιριάζουν."));
      return;
    }
    setBusy(true);
    try {
      await onChangePassword(password);
      setPassword("");
      setRepeat("");
      setNote(t("Ο κωδικός πρόσβασης άλλαξε."));
    } catch (error) {
      setNote(error instanceof Error ? error.message : t("Ο κωδικός πρόσβασης δεν άλλαξε."));
    } finally {
      setBusy(false);
    }
  };

  const disableMfa = async (): Promise<void> => {
    if (!window.confirm(
      t("Να απενεργοποιηθεί το 2FA; Θα αποσυνδεθείς αμέσως. Μπορείς να το ενεργοποιήσεις ξανά αργότερα από τις ρυθμίσεις λογαριασμού.")
    )) {
      return;
    }
    setDisablingMfa(true);
    try {
      await onDisableMfa();
    } catch {
      setNote(t("Το 2FA δεν απενεργοποιήθηκε."));
      setDisablingMfa(false);
    }
  };

  const enableMfa = async (): Promise<void> => {
    setEnablingMfa(true);
    try {
      await onEnableMfa();
    } catch {
      setNote(t("Δεν ξεκίνησε η ρύθμιση 2FA."));
      setEnablingMfa(false);
    }
  };

  return (
    <section className="screen settings-screen" aria-labelledby="settings-heading">
      {back}
      <div className="screen-heading-row">
        <div>
          <h1 id="settings-heading">{t("Λογαριασμός")}</h1>
          <p>{currentUser.username}</p>
        </div>
      </div>

      <div className="settings-groups">
        <section className="settings-group">
          <div className="status-row">
            <Smartphone />
            <span>
              <strong>{currentUser.username}</strong>
              <small>{currentUser.role === "admin" ? t("Διαχειριστής") : t("Υπάλληλος")}</small>
            </span>
          </div>
        </section>

        <section className="settings-group">
          <h2>{t("Αλλαγή κωδικού πρόσβασης")}</h2>
          <label className="field">
            <span>{t("Νέος κωδικός πρόσβασης")}</span>
            <span className="password-field">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                autoComplete="new-password"
                onChange={(event) => setPassword(event.target.value)}
              />
              <button
                type="button"
                className="icon-button password-toggle"
                onClick={() => setShowPassword((value) => !value)}
                aria-label={showPassword ? t("Απόκρυψη κωδικού") : t("Εμφάνιση κωδικού")}
              >
                {showPassword ? <EyeOff /> : <Eye />}
              </button>
            </span>
          </label>
          <label className="field">
            <span>{t("Επανάληψη")}</span>
            <span className="password-field">
              <input
                type={showRepeat ? "text" : "password"}
                value={repeat}
                autoComplete="new-password"
                onChange={(event) => setRepeat(event.target.value)}
              />
              <button
                type="button"
                className="icon-button password-toggle"
                onClick={() => setShowRepeat((value) => !value)}
                aria-label={showRepeat ? t("Απόκρυψη κωδικού") : t("Εμφάνιση κωδικού")}
              >
                {showRepeat ? <EyeOff /> : <Eye />}
              </button>
            </span>
          </label>
          {note ? <p className="hint-text">{translateMessage(note)}</p> : null}
          <button
            type="button"
            className="primary-command wide"
            disabled={busy || !password || !repeat}
            onClick={() => void save()}
          >
            <span>{busy ? t("Αποθήκευση…") : t("Αλλαγή κωδικού")}</span>
          </button>
        </section>

        <section className="settings-group">
          <h2>{t("Επαλήθευση δύο παραγόντων")}</h2>
          {currentUser.mfa_enabled === 1 ? (
            <button
              type="button"
              className="settings-row danger-row"
              disabled={disablingMfa}
              onClick={() => void disableMfa()}
            >
              <ShieldOff />
              <span>
                <strong>{disablingMfa ? t("Απενεργοποίηση…") : t("Απενεργοποίηση 2FA")}</strong>
                <small>{t("Ενεργό · η απενεργοποίηση αποσυνδέει τον λογαριασμό")}</small>
              </span>
            </button>
          ) : (
            <button
              type="button"
              className="settings-row"
              disabled={enablingMfa}
              onClick={() => void enableMfa()}
            >
              <ShieldCheck />
              <span>
                <strong>{enablingMfa ? t("Έναρξη…") : t("Ενεργοποίηση 2FA")}</strong>
                <small>{t("Προαιρετική επιπλέον προστασία λογαριασμού")}</small>
              </span>
            </button>
          )}
        </section>

        <section className="settings-group">
          <button type="button" className="settings-row danger-row" onClick={onLogout}>
            <LogOut />
            <span>
              <strong>{t("Αποσύνδεση")}</strong>
            </span>
          </button>
        </section>
      </div>
    </section>
  );
}
